import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api, type RequestOptions } from '../lib/api';
import { claveDeLectura, guardarLectura, leerLectura } from '../lib/read-cache';

/**
 * Lectura de la API con sus tres estados.
 *
 * Todo lo que se pinta pasa por aquí, así que ninguna pantalla puede
 * «olvidarse» de contemplar el error o la carga: los recibe quiera o no.
 *
 * `key` es lo que decide cuándo repetir la petición. Va como cadena y no como
 * dependencia suelta porque un objeto nuevo en cada render dispararía una
 * petición por render.
 */
export interface Query<T> {
  data: T | null;
  loading: boolean;
  error: ApiError | null;
  refetch: () => Promise<void>;
}

export function useQuery<T>(
  path: string | null,
  options: RequestOptions = {},
  key: string = path ?? '',
): Query<T> {
  const cacheKey = path ? claveDeLectura(path, options.query, key) : null;
  const inicial = cacheKey ? leerLectura<T>(cacheKey) : null;

  const [data, setData] = useState<T | null>(inicial);
  const [loading, setLoading] = useState(Boolean(path) && inicial === null);
  const [error, setError] = useState<ApiError | null>(null);

  // Descarta respuestas de peticiones ya superadas: sin esto, una lenta que
  // llega tarde sobreescribe el resultado de la que el usuario está viendo.
  const generacion = useRef(0);
  const opcionesRef = useRef(options);
  opcionesRef.current = options;

  const ejecutar = useCallback(
    async (desdeCache = false) => {
      if (!path) {
        setData(null);
        setLoading(false);
        return;
      }

      const clave = claveDeLectura(path, opcionesRef.current.query, key);

      if (desdeCache) {
        const guardado = leerLectura<T>(clave);
        if (guardado !== null) {
          setData(guardado);
          setError(null);
          setLoading(false);
          return;
        }
      }

      const propia = ++generacion.current;
      setLoading(true);

      try {
        const resultado = await api<T>(path, opcionesRef.current);
        if (propia !== generacion.current) return;
        guardarLectura(clave, resultado);
        setData(resultado);
        setError(null);
      } catch (caught) {
        if (propia !== generacion.current) return;
        setError(
          caught instanceof ApiError
            ? caught
            : new ApiError('NETWORK_ERROR', 'No se pudo contactar con el servidor', 0),
        );
      } finally {
        if (propia === generacion.current) setLoading(false);
      }
    },
    [path, key],
  );

  useEffect(() => {
    // Al montar sí se mira la caché; `refetch()` se va siempre a la red.
    void ejecutar(true);
    // `key` gobierna la recarga; `ejecutar` sólo cambia con la ruta.
  }, [ejecutar, key]);

  const refetch = useCallback(() => ejecutar(false), [ejecutar]);

  return { data, loading, error, refetch };
}

/** Escritura: expone `run`, y el estado de envío para deshabilitar botones. */
export function useMutation<TInput, TOutput>(
  fn: (input: TInput) => Promise<TOutput>,
): {
  run: (input: TInput) => Promise<TOutput | null>;
  loading: boolean;
  error: ApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const run = useCallback(
    async (input: TInput) => {
      setLoading(true);
      setError(null);
      try {
        return await fn(input);
      } catch (caught) {
        const apiError =
          caught instanceof ApiError
            ? caught
            : new ApiError('NETWORK_ERROR', 'No se pudo contactar con el servidor', 0);
        setError(apiError);
        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [fn],
  );

  return { run, loading, error };
}

/**
 * Lectura por páginas, acumulando.
 *
 * `useQuery` pide una vez y pinta lo que venga, que es lo correcto para una
 * ficha y un desastre para un listado: el servidor devuelve como mucho
 * cincuenta elementos, así que una carpeta con trescientos archivos o una
 * galería con trescientas fotos se enseñaban recortadas sin decirlo. Nadie
 * podía llegar a lo que faltaba, porque no había forma de pedir la página
 * siguiente ni señal de que existiera.
 *
 * Aquí las páginas se acumulan y la condición de parada la manda el servidor
 * en `nextOffset`. **No se deduce contando lo recibido**: una página puede
 * volver corta porque un filtro de visibilidad quitó elementos después de
 * leerlos, y darla por última dejaría fuera todo lo que venga detrás.
 */
export interface PagedResponse {
  /** `offset` de la página siguiente; `null` o ausente cuando no hay más. */
  nextOffset?: number | null;
}

export interface PagedQuery<T, R = PagedResponse> {
  items: T[];
  /**
   * La última respuesta recibida, para lo que la acompaña y no se pagina: el
   * explorador saca de aquí la carpeta actual, su breadcrumb y sus subcarpetas,
   * que vienen con cada página y no se acumulan.
   */
  last: R | null;
  /** Primera página: todavía no hay nada que pintar. */
  loading: boolean;
  /** Páginas siguientes: ya hay elementos en pantalla y llegan más. */
  loadingMore: boolean;
  error: ApiError | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

export function usePagedQuery<T, R extends PagedResponse = PagedResponse>(
  path: string | null,
  select: (respuesta: R) => T[],
  options: RequestOptions = {},
  key: string = path ?? '',
): PagedQuery<T, R> {
  const [items, setItems] = useState<T[]>([]);
  const [last, setLast] = useState<R | null>(null);
  const [offset, setOffset] = useState<number | null>(0);
  const [loading, setLoading] = useState(Boolean(path));
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  /*
   * Descarta respuestas de peticiones ya superadas. Sin esto, cambiar de
   * espacio o de búsqueda mientras baja una página tardía mezcla en la misma
   * lista elementos de dos consultas distintas.
   */
  const generacion = useRef(0);
  // La página que ya se está pidiendo, para que dos disparos seguidos del
  // centinela no traigan la misma dos veces.
  const pidiendo = useRef<number | null>(null);
  const opcionesRef = useRef(options);
  opcionesRef.current = options;
  const selectRef = useRef(select);
  selectRef.current = select;

  const pedir = useCallback(
    async (desde: number, propia: number) => {
      if (!path) return;
      if (pidiendo.current === desde) return;
      pidiendo.current = desde;

      if (desde === 0) setLoading(true);
      else setLoadingMore(true);

      try {
        const opciones = opcionesRef.current;
        const respuesta = await api<R>(path, {
          ...opciones,
          query: { ...opciones.query, offset: desde },
        });
        if (propia !== generacion.current) return;

        const pagina = selectRef.current(respuesta);
        setItems((actuales) => (desde === 0 ? pagina : [...actuales, ...pagina]));
        setLast(respuesta);
        setOffset(respuesta.nextOffset ?? null);
        setError(null);
      } catch (caught) {
        if (propia !== generacion.current) return;
        setError(
          caught instanceof ApiError
            ? caught
            : new ApiError('NETWORK_ERROR', 'No se pudo contactar con el servidor', 0),
        );
        // Se suelta la reserva: si no, reintentar no volvería a pedir nada.
        pidiendo.current = null;
      } finally {
        if (propia === generacion.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [path],
  );

  const refetch = useCallback(async () => {
    const propia = ++generacion.current;
    pidiendo.current = null;
    setItems([]);
    setLast(null);
    setOffset(0);
    setError(null);

    if (!path) {
      setLoading(false);
      return;
    }

    await pedir(0, propia);
  }, [pedir, path]);

  useEffect(() => {
    void refetch();
    // `key` gobierna la recarga; `refetch` sólo cambia con la ruta.
  }, [refetch, key]);

  const loadMore = useCallback(async () => {
    if (offset === null || loading || loadingMore) return;
    await pedir(offset, generacion.current);
  }, [offset, loading, loadingMore, pedir]);

  return {
    items,
    last,
    loading,
    loadingMore,
    error,
    // Mientras baja la primera página no hay «más» que pedir: hay lo primero.
    // Dicho de otro modo, `hasMore` sólo habla de lo que YA no está en pantalla.
    hasMore: !loading && offset !== null,
    loadMore,
    refetch,
  };
}
